import { useState } from "react";
import { X, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { track } from "@/lib/track";

/**
 * Unified Login / Signup modal with Google OAuth button.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   lang: "ar" | "en"
 *   initialMode: "login" | "signup"
 */
export default function AuthModal({ open, onClose, lang = "ar", initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();

  if (!open) return null;

  const t = lang === "ar"
    ? {
        login: "تسجيل الدخول",
        signup: "إنشاء حساب",
        emailLbl: "البريد الإلكتروني",
        passLbl: "كلمة المرور",
        nameLbl: "الاسم",
        googleBtn: "متابعة باستخدام Google",
        or: "أو",
        submit: mode === "login" ? "دخول" : "إنشاء الحساب",
        switchToSignup: "ليس لديك حساب؟ أنشئ واحدًا",
        switchToLogin: "لديك حساب؟ سجّل الدخول",
        welcome: "+ ٣ اقتراحات AI مجانية عند التسجيل 🎁",
        passHint: "٨ أحرف على الأقل",
      }
    : {
        login: "Sign in",
        signup: "Create account",
        emailLbl: "Email",
        passLbl: "Password",
        nameLbl: "Name",
        googleBtn: "Continue with Google",
        or: "or",
        submit: mode === "login" ? "Sign in" : "Create account",
        switchToSignup: "No account? Create one",
        switchToLogin: "Have an account? Sign in",
        welcome: "+ 3 free AI suggestions on signup 🎁",
        passHint: "8 characters minimum",
      };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = mode === "login"
        ? await login(email, password)
        : await register(email, password, name);
      if (res.ok) {
        track(mode === "login" ? "login_complete" : "signup_complete", null, { once: false });
        onClose?.();
      } else {
        setErr(res.error || "خطأ");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      dir={lang === "ar" ? "rtl" : "ltr"}
      data-testid="auth-modal"
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-[#182138]/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md rounded-3xl bg-[#1f2a47] border border-[#2e3a5c] p-6 sm:p-8 shadow-[0_30px_80px_-20px_rgba(24,201,143,0.35)] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
      >
        <button
          onClick={onClose}
          data-testid="auth-close"
          className={`absolute top-3 ${lang === "ar" ? "left-3" : "right-3"} p-2 rounded-full text-white/60 hover:text-white hover:bg-white/5`}
          aria-label="close"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl font-extrabold text-white">
          {mode === "login" ? t.login : t.signup}
        </h2>
        <p className="mt-1 text-xs text-[#18c98f]">{t.welcome}</p>

        {/* Google button */}
        <button
          type="button"
          data-testid="auth-google-btn"
          onClick={startGoogleLogin}
          className="mt-5 w-full inline-flex items-center justify-center gap-2.5 px-4 h-11 rounded-full bg-white text-[#182138] text-sm font-extrabold hover:brightness-95 transition"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.25 1.4-1.01 2.58-2.17 3.37v2.8h3.5c2.08-1.9 3.29-4.74 3.29-8.41z"/><path fill="#34A853" d="M12 24c2.97 0 5.46-.98 7.28-2.66l-3.5-2.7c-.98.66-2.23 1.06-3.78 1.06-2.87 0-5.29-1.94-6.16-4.53H2.18v2.84C3.99 21.3 7.69 24 12 24z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.37-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.72 11.72 0 0 0 .89 12c0 1.87.42 3.64 1.29 5.21l3.66-3.12z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.07.56 4.21 1.65l3.14-3.14C17.46 1.57 14.97 0 12 0 7.69 0 3.99 2.7 2.18 6.63l3.66 2.84C6.71 6.9 9.13 4.75 12 4.75z"/></svg>
          {t.googleBtn}
        </button>

        <div className="my-4 flex items-center gap-3 text-[11px] text-white/40">
          <span className="h-px flex-1 bg-[#2e3a5c]" />
          <span>{t.or}</span>
          <span className="h-px flex-1 bg-[#2e3a5c]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <label className="block">
              <span className="text-[11px] text-white/60">{t.nameLbl}</span>
              <div className="mt-1 flex items-center gap-2 h-11 rounded-xl bg-[#182138] border border-[#2e3a5c] px-3 focus-within:border-[#18c98f]">
                <UserIcon className="h-4 w-4 text-white/40" />
                <input
                  data-testid="auth-name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-white outline-none"
                  required
                />
              </div>
            </label>
          )}
          <label className="block">
            <span className="text-[11px] text-white/60">{t.emailLbl}</span>
            <div className="mt-1 flex items-center gap-2 h-11 rounded-xl bg-[#182138] border border-[#2e3a5c] px-3 focus-within:border-[#18c98f]">
              <Mail className="h-4 w-4 text-white/40" />
              <input
                data-testid="auth-email"
                type="email"
                autoComplete="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent text-sm text-white outline-none"
                required
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[11px] text-white/60">{t.passLbl}</span>
            <div className="mt-1 flex items-center gap-2 h-11 rounded-xl bg-[#182138] border border-[#2e3a5c] px-3 focus-within:border-[#18c98f]">
              <Lock className="h-4 w-4 text-white/40" />
              <input
                data-testid="auth-password"
                type="password"
                dir="ltr"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                className="flex-1 bg-transparent text-sm text-white outline-none"
                required
              />
            </div>
            {mode === "signup" && (
              <span className="mt-1 block text-[10px] text-white/40">{t.passHint}</span>
            )}
          </label>

          {err && (
            <div data-testid="auth-error" className="rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-xs p-3">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 h-11 rounded-full bg-[#18c98f] text-white text-sm font-extrabold hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t.submit}
          </button>
        </form>

        <button
          onClick={() => { setErr(""); setMode(mode === "login" ? "signup" : "login"); }}
          data-testid="auth-switch"
          className="mt-4 w-full text-center text-xs text-[#18c98f] hover:underline"
        >
          {mode === "login" ? t.switchToSignup : t.switchToLogin}
        </button>
      </div>
    </div>
  );
}
